import type { SpotRank } from '../types/spot.js';

export interface RankDefinition {
  value: SpotRank;
  label: string;
  minScore: number;
  color: string;
  icon: string;
}

export const RANKS: RankDefinition[] = [
  { value: 'none', label: 'ランクなし', minScore: 0, color: '#9E9E9E', icon: '⚪' },
  { value: 'bronze', label: 'ブロンズ', minScore: 10, color: '#CD7F32', icon: '🥉' },
  { value: 'silver', label: 'シルバー', minScore: 50, color: '#C0C0C0', icon: '🥈' },
  { value: 'gold', label: 'ゴールド', minScore: 200, color: '#FFD700', icon: '🥇' },
  { value: 'platinum', label: 'プラチナ', minScore: 1000, color: '#E5E4E2', icon: '💎' },
];

export const RANK_MAP = new Map<SpotRank, RankDefinition>(RANKS.map((r) => [r.value, r]));

/**
 * スコアからランクを計算する
 * スコア = totalCheckins * 1.0 + totalLikes * 2.0
 */
export function calculateRank(score: number): SpotRank {
  for (let i = RANKS.length - 1; i >= 0; i--) {
    if (score >= RANKS[i].minScore) {
      return RANKS[i].value;
    }
  }
  return 'none';
}

export function getRankLabel(rank: SpotRank): string {
  return RANK_MAP.get(rank)?.label ?? rank;
}

// ─────────────────────────────────────────────
// ユーザーランク定数
// ─────────────────────────────────────────────

/** チェックインランク: 各レベルの累計チェックイン数の閾値 */
export const CHECKIN_RANK_THRESHOLDS: Record<string, number> = {
  lv5: 500,
  lv4: 200,
  lv3: 100,
  lv2: 30,
  lv1: 1,
};

/** チェックインランク: 表示名 */
export const CHECKIN_RANK_LABELS: Record<string, string> = {
  lv5: 'ロードマスター',
  lv4: 'エクスプローラー',
  lv3: 'トラベラー',
  lv2: 'ライダー',
  lv1: 'ビギナー',
  none: '−',
};

/** スポットメイカーランク: 各レベルの最低スコア */
export const SPOT_MAKER_RANK_MIN_SCORES: Record<string, number> = {
  lv5: 1000,
  lv4: 500,
  lv3: 200,
  lv2: 100,
  lv1: 10,
};

/**
 * スポットメイカーランク: パーセンタイル下限値
 * PERCENT_RANK()は0=最低, 1=最高。上位N%は PERCENT_RANK >= 1-N/100
 */
export const SPOT_MAKER_RANK_PERCENTILES: Record<string, number> = {
  lv5: 0.99, // 上位 1%
  lv4: 0.9, // 上位 2〜10%
  lv3: 0.7, // 上位 11〜30%
  lv2: 0.4, // 上位 31〜60%
  lv1: 0, // 投稿1件以上（パーセンタイル条件なし）
};

/** スポットメイカーランク: 表示名 */
export const SPOT_MAKER_RANK_LABELS: Record<string, string> = {
  lv5: 'マイスター',
  lv4: 'ナビゲーター',
  lv3: 'アドバイザー',
  lv2: 'マーカー',
  lv1: 'ルーキー',
  none: '−',
};

/**
 * rankScore を 0.0〜5.0 の星スコアに変換する（対数スケール）
 * rankScore=10(bronze閾値) → ~1.7, 50(silver) → ~2.8, 200(gold) → ~3.8, 1000(platinum) → 5.0
 */
export function calculateStarScore(rankScore: number): number {
  if (rankScore <= 0) return 0.0;
  const score = (Math.log(rankScore + 1) / Math.log(1001)) * 5;
  return Math.round(score * 10) / 10;
}
